import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface Props {
  article: Article;
  layout?: "grid" | "row" | "plain";
  showLead?: boolean;
}

export default function ArticleCard({ article, layout = "grid", showLead = false }: Props) {
  if (layout === "plain") {
    return (
      <Link to={`/article/${article.slug}`} className="group block">
        <div className="flex items-start gap-3 border-b border-foreground-900/10 py-3 last:border-0">
          <span className="mt-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-primary-100 font-label text-sm text-primary-700">
            <i className="ri-newspaper-line"></i>
          </span>
          <div className="min-w-0">
            <h4 className="line-clamp-2 text-[15px] font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
              {article.title}
            </h4>
            <p className="mt-1 text-xs text-foreground-500">
              {article.date} · {article.views}阅读
            </p>
          </div>
        </div>
      </Link>
    );
  }

  if (layout === "row") {
    return (
      <article className="group flex gap-4">
        <Link
          to={`/article/${article.slug}`}
          className="relative block h-[84px] w-[124px] shrink-0 overflow-hidden rounded-md md:h-[96px] md:w-[152px]"
        >
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} 深度盘点`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="min-w-0 py-0.5">
          <div className="flex items-center gap-2">
            <Link
              to={`/category/${article.catKey}`}
              className="whitespace-nowrap rounded-sm bg-secondary-100 px-1.5 py-0.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
            >
              {article.catName}
            </Link>
            <span className="whitespace-nowrap text-xs text-foreground-500">{article.date}</span>
          </div>
          <Link to={`/article/${article.slug}`}>
            <h3 className="mt-1.5 line-clamp-2 text-[15px] font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
              {article.title}
            </h3>
          </Link>
          {showLead && (
            <p className="mt-1.5 hidden line-clamp-2 text-sm leading-6 text-foreground-600 sm:block">
              {article.lead}
            </p>
          )}
        </div>
      </article>
    );
  }

  // grid
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-foreground-900/10 bg-background-100/60">
      <Link
        to={`/article/${article.slug}`}
        className="relative block aspect-[16/10] overflow-hidden"
      >
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} 人物盘点`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-center justify-between">
          <Link
            to={`/category/${article.catKey}`}
            className="whitespace-nowrap rounded-sm bg-secondary-100 px-1.5 py-0.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            {article.catName}
          </Link>
          <span className="whitespace-nowrap text-xs text-foreground-500">
            {article.views}阅读
          </span>
        </div>
        <Link to={`/article/${article.slug}`}>
          <h3 className="mt-2.5 line-clamp-2 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
            {article.title}
          </h3>
        </Link>
        {showLead && (
          <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{article.lead}</p>
        )}
        <div className="mt-auto pt-3">
          <p className="flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-time-line"></i>
            {article.date}
          </p>
        </div>
      </div>
    </article>
  );
}